import React, { FC, useState, useEffect } from "react";
import LocationInputSimple from "../HeroSearchForm/LocationInputSimple";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";

export type SearchTab = "Stays" | "Experiences" | "Cars" | "Flights";

interface HeroSearchFormSmallProps {
  defaultFieldFocus?: "location" | "dates" | "guests";
  onTabChange?: (tab: SearchTab) => void;
  defaultTab?: SearchTab;
  onClose?: () => void;
}

const HeroSearchFormSmall: FC<HeroSearchFormSmallProps> = ({
  defaultFieldFocus,
  onTabChange,
  defaultTab = "Stays",
  onClose,
}) => {
  const storedSearchData = useSelector(
    (state: RootState) => state.search.searchedData
  );
  
  const {
    latitude,
    longitude,
    locationData,
    startDate: start,
    endDate: end,
    totalGuests,
    priceMin,
    priceMax,
  } = storedSearchData || {};

  const [selectedLocation, setSelectedLocation] = useState<string>("");
  const [selectedRoomTypes, setSelectedRoomTypes] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 5000]);
  const [showPriceSlider, setShowPriceSlider] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [guests, setGuests] = useState<number>(0);

  const PROPERTY_TYPE_ID_TO_NAME: Record<string, string> = {
    "1": "Entire Property",
    "2": "Shared Property",
  };

  const PROPERTY_TYPE_NAME_TO_ID: Record<string, string> = {
    "Entire Property": "1",
    "Shared Property": "2",
  };

    useEffect(() => {
    console.log("🏠 HeroSearchFormSmall - selectedRoomTypes:", selectedRoomTypes);
  }, [selectedRoomTypes]);

  // Parse date helper function
  const parseValidDate = (dateString: string | undefined | null): Date | null => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  };

  // Load saved data from Redux store on mount and when it changes
  useEffect(() => {
    if (locationData) {
      const locationString = locationData.neighborhood 
        ? `${locationData.neighborhood}, ${locationData.city}, ${locationData.country}`
        : `${locationData.city}, ${locationData.country}`;
      setSelectedLocation(locationString);
    }

    

    // Set dates from stored search data
    if (start) {
      setStartDate(parseValidDate(start));
    }
    if (end) {
      setEndDate(parseValidDate(end));
    }

    // Set guests from stored search data
    if (totalGuests) {
      setGuests(totalGuests);
    }

    // Set price range from stored search data
    if (priceMin !== undefined || priceMax !== undefined) {
      setPriceRange([
        priceMin ?? 0,
        priceMax ?? 5000
      ]);
    }

   if (storedSearchData?.propertyTypeId) {
    const propertyTypeIds = storedSearchData.propertyTypeId.split(",").filter(Boolean);
const propertyTypeNames = propertyTypeIds
  .map((id: string) => PROPERTY_TYPE_ID_TO_NAME[id])
  .filter(Boolean);
    
    console.log("🔄 Loading property types from Redux:", propertyTypeIds, "→", propertyTypeNames);
    setSelectedRoomTypes(propertyTypeNames);
  } else {
    setSelectedRoomTypes([]); // Clear if no property types stored
  }
}, [locationData, start, end, totalGuests, priceMin, priceMax, storedSearchData?.propertyTypeId]);
 

  // Format date for display
  const formatDate = (date: Date | null): string => {
    if (!date) return "";
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  };

  // Toggle room type selection
  const handleRoomTypeClick = (e: React.MouseEvent, type: string) => {
    e.stopPropagation();
    e.preventDefault();
    
    setSelectedRoomTypes(prev => {
      const newTypes = prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type];
      
      console.log('🔘 Room type clicked:', type);
    console.log('🔘 Previous types:', prev);
    console.log('🔘 New types:', newTypes);
      return newTypes;
    });
  };

  // Handle range slider change
  const handleRangeChange = (e: React.ChangeEvent<HTMLInputElement>, isMin: boolean) => {
    const value = Number(e.target.value);
    
    if (isMin) {
      if (value <= priceRange[1]) {
        setPriceRange([value, priceRange[1]]);
      }
    } else {
      if (value >= priceRange[0]) {
        setPriceRange([priceRange[0], value]);
      }
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedRoomTypes([]);
    setPriceRange([0, 5000]);
    setShowPriceSlider(false);
  };

  useEffect(() => {
  console.log("🔍 Passing to LocationInputSimple - propertyTypes:", selectedRoomTypes);
}, [selectedRoomTypes]);

  const hasActiveFilters = selectedRoomTypes.length > 0 || priceRange[0] > 0 || priceRange[1] < 5000;

  return (
    <div className="w-full">
      {/* Header Section */}
      <div className="w-full bg-white dark:bg-neutral-800 rounded-t-3xl px-4 sm:px-6 pt-5 pb-4 shadow-lg">
        <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white">
          Find your next stay
        </h3>
        
        {/* Display current search info if available */}
        {(startDate || endDate || guests > 0 || priceRange[0] > 0 || priceRange[1] < 5000) && (
          <div className="mt-3 flex flex-wrap gap-2 text-xs text-gray-600 dark:text-gray-400">
            {startDate && endDate && (
              <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-neutral-700 rounded-full">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>{formatDate(startDate)} - {formatDate(endDate)}</span>
              </div>
            )}
            {guests > 0 && (
              <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-neutral-700 rounded-full">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span>{guests} {guests === 1 ? 'guest' : 'guests'}</span>
              </div>
            )}
            {(priceRange[0] > 0 || priceRange[1] < 5000) && (
              <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-neutral-700 rounded-full">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>${priceRange[0]} - ${priceRange[1]}</span>
              </div>
            )}
          </div>
        )}
      </div>

      

      {/* Search Section */}
      <div className="w-full bg-white dark:bg-neutral-800 rounded-b-3xl px-4 sm:px-6 pb-5 shadow-lg">

        

        {/* Location Input */}
        <div className="relative z-50 mb-4">
          <LocationInputSimple
            value={selectedLocation}
            setValue={setSelectedLocation}
            onSearchComplete={onClose}
            propertyTypes={selectedRoomTypes}
            priceRange={priceRange}
            initialLat={latitude}              
            initialLng={longitude}             
            initialLocationData={locationData} 
          />
        </div>
        
        {/* Filters Section */}
        <div className="space-y-4">
          {/* Property Type Filter */}
          <div className="border-t border-gray-200 dark:border-neutral-700 pt-4">
            <label className="text-sm font-semibold text-gray-900 dark:text-white mb-3 block">
              Property type
            </label>
            <div className="flex flex-wrap gap-2">
              <button 
                type="button"
                onClick={(e) => handleRoomTypeClick(e, "Entire Property")}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl transition-all ${
                  selectedRoomTypes.includes("Entire Property")
                    ? "bg-[#F56948] text-white shadow-lg shadow-[#F56948]/25"
                    : "text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-neutral-700 hover:bg-gray-100 dark:hover:bg-neutral-600 border border-gray-200 dark:border-neutral-600"
                }`}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                Entire Property
                {selectedRoomTypes.includes("Entire Property") && (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
              <button 
                type="button"
                onClick={(e) => handleRoomTypeClick(e, "Shared Property")}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl transition-all ${
                  selectedRoomTypes.includes("Shared Property")
                    ? "bg-[#F56948] text-white shadow-lg shadow-[#F56948]/25"
                    : "text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-neutral-700 hover:bg-gray-100 dark:hover:bg-neutral-600 border border-gray-200 dark:border-neutral-600"
                }`}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                </svg>
                Shared Property
                {selectedRoomTypes.includes("Shared Property") && (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Price Range Filter */}
          <div className="border-t border-gray-200 dark:border-neutral-700 pt-4">
            <button
              type="button"
              onClick={() => setShowPriceSlider(!showPriceSlider)}
              className="w-full flex items-center justify-between text-left group"
            >
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                Price range
              </span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-[#F56948]">
                  ${priceRange[0]} - ${priceRange[1]}
                </span>
                <svg
                  className={`w-5 h-5 text-gray-400 transition-transform ${showPriceSlider ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>

            {showPriceSlider && (
              <div className="mt-4 p-4 bg-gray-50 dark:bg-neutral-700/50 rounded-xl">
                {/* Dual Range Slider */}
                <div className="relative h-10 flex items-center">
                  <div className="absolute w-full h-2 bg-gray-200 dark:bg-neutral-600 rounded-full" />
                  <div 
                    className="absolute h-2 bg-[#F56948] rounded-full transition-all"
                    style={{
                      left: `${(priceRange[0] / 5000) * 100}%`,
                      right: `${100 - (priceRange[1] / 5000) * 100}%`
                    }}
                  />
                  <input
                    type="range"
                    min="0"
                    max="5000"
                    step="50"
                    value={priceRange[0]}
                    onChange={(e) => handleRangeChange(e, true)}
                    className="absolute w-full appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-[#F56948] [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-lg [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-[#F56948] [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:shadow-lg"
                    style={{ zIndex: priceRange[0] > 5000 - 1000 ? 5 : 3 }}
                  />
                  <input
                    type="range"
                    min="0"
                    max="5000"
                    step="50"
                    value={priceRange[1]}
                    onChange={(e) => handleRangeChange(e, false)}
                    className="absolute w-full appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-[#F56948] [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-lg [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-[#F56948] [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:shadow-lg"
                    style={{ zIndex: 4 }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <div className="border-t border-gray-200 dark:border-neutral-700 pt-4">
              <button
                type="button"
                onClick={clearAllFilters}
                className="text-sm font-medium text-[#F56948] hover:text-[#e2583d] transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear all filters
              </button>
            </div>
          )}

          {/* Close Button - At bottom */}
          {onClose && (
            <div className="border-t border-gray-200 dark:border-neutral-700 pt-4">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  console.log('Close button clicked');
                  onClose();
                }}
                className="w-full px-4 py-3 text-sm font-medium text-white bg-[#F56948] hover:bg-[#e2583d] rounded-xl transition-colors flex items-center justify-center gap-2 shadow-lg shadow-[#F56948]/25"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeroSearchFormSmall;